/**
 * Admin User Management API Types
 * 
 * Types specific to the admin user management endpoints
 * These match the actual API response structure from /admin/users
 */

export type UserGender = 'MALE' | 'FEMALE' | 'OTHER' | 'PREFER_NOT_TO_SAY';
export type UserTier = 'FREE' | 'PREMIUM' | 'VIP';

/**
 * Admin User Interface - Matches actual API response
 */
export interface AdminUser {
  userId: string;
  name: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: UserGender;
  tier: UserTier;
  registrationDate: string;
  lastLogin: string | null;
}

/**
 * Admin User Detail Interface - Includes audit fields
 * Used for GET /admin/users/{userId} response
 */
export interface AdminUserDetail extends AdminUser {
  profileImageUrl?: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  isDeleted: boolean;
  deletedAt: string | null;
  deletedBy: string | null;
}

/**
 * Admin Users List Query Parameters
 */
export interface AdminUsersQueryParams {
  name?: string;
  email?: string;
  phone?: string;
  tier?: UserTier | '';
  registration_date_from?: string;
  registration_date_to?: string;
  page?: number;
  size?: number;
}

/**
 * Pagination metadata from API
 */
export interface AdminUsersPagination {
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Admin Users List Response Data
 */
export interface AdminUsersData {
  items: AdminUser[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Complete API Response Structure
 */
export interface AdminUsersResponse {
  success: boolean;
  message: string;
  data: AdminUsersData;
  timestamp: string;
}

/**
 * User Update Request
 * For PUT /admin/users/{userId}
 */
export interface UserUpdateRequest {
  name?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string; // ISO Date format: yyyy-MM-dd
  gender?: UserGender;
  tier?: UserTier;
  isActive?: boolean;
}

/**
 * User Soft Delete Request
 * For DELETE /admin/users/{userId}
 */
export interface UserSoftDeleteRequest {
  reason: string; // Required, 5-500 chars
}

/**
 * User Soft Delete Response
 */
export interface UserSoftDeleteResponse {
  userId: string;
  email: string;
  name: string;
  deletedAt: string;
  deletedBy: string;
  reason: string;
  isDeleted: boolean;
}

/**
 * User Restore Response
 * For POST /admin/users/{userId}/restore
 */
export interface UserRestoreResponse extends AdminUser {
  profileImageUrl?: string | null;
}

/**
 * Deleted Users List Response Data
 * For GET /admin/users/deleted
 */
export interface DeletedUsersData {
  items: AdminUser[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * User Tier Options for Filters
 */
export const USER_TIERS: { value: UserTier | ''; label: string }[] = [
  { value: '', label: 'All Tiers' },
  { value: 'FREE', label: 'Free' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'VIP', label: 'VIP' },
];

/**
 * Gender Options for Filters
 */
export const USER_GENDERS: { value: UserGender | ''; label: string }[] = [
  { value: '', label: 'All Genders' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

/**
 * Helper function to format date for API
 */
export const formatDateForAPI = (date: Date): string => {
  return date.toISOString().split('T')[0]; // YYYY-MM-DD
};

/**
 * Parse admin-user API date strings safely.
 *
 * Backend returns ISO strings **without timezone** (e.g. `2026-01-14T13:29:44.224622`).
 * We want to display the **same wall-clock time** (13:29) regardless of the user's timezone.
 *
 * Strategy:
 * - If no timezone is present, treat the value as UTC by appending `Z`
 * - Format using `timeZone: 'UTC'` so it doesn't shift for the user
 * - Trim fractional seconds down to milliseconds (3 digits) for reliable parsing
 */
const parseAdminUserDate = (
  dateString: string
): { date: Date; timeZone?: 'UTC' } | null => {
  if (!dateString) return null;

  const hasTimeZone = /([zZ]|[+-]\d{2}:\d{2})$/.test(dateString);

  // Date-only string: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const iso = hasTimeZone ? dateString : `${dateString}T00:00:00Z`;
    const date = new Date(iso);
    return isNaN(date.getTime()) ? null : { date, timeZone: 'UTC' };
  }

  // Datetime string: YYYY-MM-DDTHH:mm:ss(.fraction)?(Z|±hh:mm)?
  const match = dateString.match(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.(\d+))?([zZ]|[+-]\d{2}:\d{2})?$/
  );
  if (match) {
    const base = match[1];
    const fraction = match[3];
    const tz = match[4];

    const ms = fraction ? fraction.slice(0, 3).padEnd(3, '0') : undefined;
    const rebuilt = `${base}${ms ? `.${ms}` : ''}${tz ? tz : 'Z'}`;
    const date = new Date(rebuilt);
    if (isNaN(date.getTime())) return null;

    // No timezone in source -> treat as UTC for display
    return tz ? { date } : { date, timeZone: 'UTC' };
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : { date };
};

/**
 * Helper function to format date for display
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '-';
  const parsed = parseAdminUserDate(dateString);
  if (!parsed) return '-';

  return parsed.date.toLocaleDateString('en-US', {
    ...(parsed.timeZone ? { timeZone: parsed.timeZone } : {}),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Helper function to format datetime for display
 */
export const formatDateTimeForDisplay = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const parsed = parseAdminUserDate(dateString);
  if (!parsed) return 'Never';

  return parsed.date.toLocaleString('en-US', {
    ...(parsed.timeZone ? { timeZone: parsed.timeZone } : {}),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Helper function to format time for display (HH:MM AM/PM).
 */
export const formatTimeForDisplay = (dateString: string | null): string => {
  if (!dateString) return '-';
  const parsed = parseAdminUserDate(dateString);
  if (!parsed) return '-';

  return parsed.date.toLocaleTimeString('en-US', {
    ...(parsed.timeZone ? { timeZone: parsed.timeZone } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
};
